import { useState } from "react";
import { TextField } from "../../common/inputFiled";
import { LocationModal } from "../../profileComponents/locationModel";
import toast from "react-hot-toast";

const CURRENT_YEAR = new Date().getFullYear();
const onlyDigits = (val) => String(val).replace(/\D+/g, "");

const INITIAL_FORM = {
  education_current_studying: "0",
  edu_institution_name: "",
  edu_pin_code: "",
  edu_city_region_country: "",
  edu_address_pincode: "",
  edu_address_city: "",
  edu_address_region: "",
  edu_address_country: "",
  course_name: "",
  edu_start_date: "",
  edu_end_date: "",
};

export default function AppSetupEducationStep({ onSubmit, loading }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [showLocationModal, setShowLocationModal] = useState(false);

  const isCurrentStudying = Number(formData.education_current_studying) === 1;

  const clearError = (key) => {
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleChange = (field, value) => {
    let normalized = typeof value === "string" ? value.replace(/^\s+/, "") : value;
    if (field === "course_name") {
      normalized = String(normalized || "").replace(/[^a-zA-Z\s]/g, "");
    }
    setFormData((prev) => {
      const next = { ...prev, [field]: normalized };
      if (field === "edu_pin_code") next.edu_address_pincode = normalized;
      return next;
    });
    clearError(field);
  };

  const handlePlaceSelect = ({ place, location, country, post_code }) => {
    const source = location || place || {};
    const placeName =
      place?.place_name || place?.name || source?.place_name || source?.name || source?.city || "";
    const state = source?.state || source?.region || place?.state || "";
    const countryValue = country || source?.country || "";
    const cityRegionCountry = [location?.city || placeName, state, countryValue].filter(Boolean).join(", ");

    setFormData((prev) => ({
      ...prev,
      edu_city_region_country: cityRegionCountry,
      edu_address_pincode: post_code || "",
      edu_pin_code: post_code || "",
      edu_address_city: location?.city || placeName || "",
      edu_address_region: state || "",
      edu_address_country: countryValue || "",
    }));
    setShowLocationModal(false);
    toast.success("Location updated");
  };

  const parseMonth = (val) => {
    if (!val) return null;
    if (val.includes("-")) {
      const [y, m] = val.split("-");
      return { y: Number(y), m: Number(m) };
    }
    if (val.includes("/")) {
      const [m, y] = val.split("/");
      return { y: Number(y), m: Number(m) };
    }
    return null;
  };

  const validate = () => {
    const nextErrors = {};
    const required = [
      ["edu_institution_name", "Name of the Institution"],
      ["edu_pin_code", "Pincode"],
      ["edu_city_region_country", "City, Region and Country"],
      ["course_name", "Course Name"],
      ["edu_start_date", "Start Date"],
    ];

    required.forEach(([key, label]) => {
      if (!String(formData[key] || "").trim()) nextErrors[key] = `Enter ${label}`;
    });

    if (!isCurrentStudying && !String(formData.edu_end_date || "").trim()) {
      nextErrors.edu_end_date = "Enter End Date";
    }

    const pin = formData.edu_address_pincode || formData.edu_pin_code;
    if (pin && onlyDigits(pin).length !== 6) {
      nextErrors.edu_pin_code = "Pin Code must be exactly 6 digits";
    }

    const start = parseMonth(formData.edu_start_date);
    const end = parseMonth(formData.edu_end_date);
    if (start && (start.y < 1950 || start.y > CURRENT_YEAR)) {
      nextErrors.edu_start_date = `Start Date year must be between 1950 and ${CURRENT_YEAR}`;
    }
    if (end && (end.y < 1950 || end.y > CURRENT_YEAR)) {
      nextErrors.edu_end_date = `End Date year must be between 1950 and ${CURRENT_YEAR}`;
    }
    if (!isCurrentStudying && start && end) {
      if (end.y < start.y || (end.y === start.y && end.m < start.m)) {
        nextErrors.edu_end_date = "End date must be the same or after start date";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    onSubmit(formData, isCurrentStudying);
  };

  return (
    <>
      {showLocationModal && (
        <LocationModal
          show={showLocationModal}
          onClose={() => setShowLocationModal(false)}
          onSelect={handlePlaceSelect}
          initialPincode={formData.edu_pin_code || ""}
          initialPlaceName={formData.edu_institution_name || ""}
        />
      )}

      <div className="mobile-scroll-y h-[var(--app-height,100dvh)] bg-[#F3F0FF] px-4 py-6 sm:py-8">
        <div className="mx-auto w-full max-w-[520px] p-4 sm:p-8">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-semibold text-gray-900">Education</h2>
            <p className="mt-2 text-sm text-gray-600">Add your primary education details.</p>
          </div>

          <form
            className="space-y-4 rounded-3xl border border-violet-100 bg-white p-6 shadow-xl shadow-violet-200/30"
            onSubmit={handleSubmit}
            noValidate
          >
            <label className="flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3">
              <span className="text-sm font-medium text-gray-800">Currently Studying</span>
              <button
                type="button"
                role="switch"
                aria-checked={isCurrentStudying}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    education_current_studying: isCurrentStudying ? "0" : "1",
                    edu_end_date: isCurrentStudying ? prev.edu_end_date : "",
                  }))
                }
                className={`relative h-6 w-11 rounded-full transition ${isCurrentStudying ? "bg-[#6938EF]" : "bg-gray-300"}`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${isCurrentStudying ? "left-5" : "left-0.5"}`}
                />
              </button>
            </label>

            <TextField
              label="Name of the Institution"
              name="edu_institution_name"
              required
              value={formData.edu_institution_name}
              onChange={(e) => handleChange("edu_institution_name", e.target.value)}
              placeholder="Enter institution name"
              error={errors.edu_institution_name}
            />

            <TextField
              label="Pincode"
              name="edu_pin_code"
              required
              value={formData.edu_pin_code}
              onFocus={(e) => {
                e.target.blur();
                setShowLocationModal(true);
              }}
              onClick={() => setShowLocationModal(true)}
              readOnly
              rightIcon="../input-logos/location.svg"
              placeholder="Select pincode"
              error={errors.edu_pin_code}
            />

            <TextField
              label="City, Region and Country"
              name="edu_city_region_country"
              required
              value={formData.edu_city_region_country}
              disabled
              placeholder="Autofilled from pincode"
              error={errors.edu_city_region_country}
            />

            <TextField
              label="Course Name"
              name="course_name"
              required
              value={formData.course_name}
              onChange={(e) => handleChange("course_name", e.target.value)}
              placeholder="Enter course name"
              error={errors.course_name}
            />

            <TextField
              label="Start Date"
              name="edu_start_date"
              required
              type="month"
              value={formData.edu_start_date}
              onChange={(e) => handleChange("edu_start_date", e.target.value)}
              error={errors.edu_start_date}
            />

            {!isCurrentStudying && (
              <TextField
                label="End Date"
                name="edu_end_date"
                required
                type="month"
                value={formData.edu_end_date}
                onChange={(e) => handleChange("edu_end_date", e.target.value)}
                error={errors.edu_end_date}
              />
            )}

            <button
              type="submit"
              disabled={loading}
              className="submit-button w-full rounded-xl py-3.5 text-sm font-semibold shadow-lg shadow-violet-500/25 disabled:opacity-60"
            >
              {loading ? "Saving..." : "Continue"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
